import { mrpLocationChipStyles, type MrpLocation } from "@/lib/mrp";
import { cn } from "@/lib/utils";

import type { TransferBoardLocation } from "./transfersWorkbenchTypes";

function isMrpLocationCode(code: string): code is MrpLocation {
  return code === "sgs" || code === "suntronic" || code === "kodiak";
}

/** Hub MRP planning sites shown first on transfer filter rows (Kodiak, SGS, Suntronic). */
const PROMINENT_TRANSFER_SITE_ORDER: readonly MrpLocation[] = ["kodiak", "sgs", "suntronic"];

function prominentSiteRank(location: TransferBoardLocation): number {
  const code = location.mrp_site_code?.trim().toLowerCase() ?? "";
  if (!isMrpLocationCode(code)) return Number.POSITIVE_INFINITY;
  const index = PROMINENT_TRANSFER_SITE_ORDER.indexOf(code);
  return index >= 0 ? index : Number.POSITIVE_INFINITY;
}

export function isProminentTransferPlanningSite(location: TransferBoardLocation): boolean {
  return prominentSiteRank(location) < Number.POSITIVE_INFINITY;
}

export function sortTransferBoardLocationsForFilters(
  locations: readonly TransferBoardLocation[],
): TransferBoardLocation[] {
  return [...locations].sort((left, right) => {
    const rankLeft = prominentSiteRank(left);
    const rankRight = prominentSiteRank(right);
    if (rankLeft !== rankRight) return rankLeft - rankRight;
    return buildTransferLocationLabel(left, left.id).localeCompare(
      buildTransferLocationLabel(right, right.id),
      undefined,
      { sensitivity: "base" },
    );
  });
}

export function buildTransferLocationLabel(
  location: TransferBoardLocation | undefined,
  fallbackId: number,
): string {
  if (location == null) return `Location ${String(fallbackId)}`;
  const abbreviation = location.abbreviation?.trim() ?? "";
  if (abbreviation !== "") return abbreviation;
  const name = location.name?.trim() ?? "";
  if (name !== "") return name;
  return `Location ${String(fallbackId)}`;
}

export function transferLocationChipStyles(
  location: TransferBoardLocation | undefined,
  fallbackId: number,
): { label: string; className: string } {
  const label = buildTransferLocationLabel(location, fallbackId);
  const siteCode = location?.mrp_site_code?.trim().toLowerCase() ?? "";
  if (isMrpLocationCode(siteCode)) {
    const chip = mrpLocationChipStyles(siteCode);
    return { label, className: chip.className };
  }
  return { label, className: "border-slate-300 bg-slate-100 text-slate-800" };
}

export function TransferLocationChip({
  location,
  locationId,
  size = "md",
}: {
  location: TransferBoardLocation | undefined;
  locationId: number;
  size?: "md" | "lg";
}): JSX.Element {
  const { label, className } = transferLocationChipStyles(location, locationId);
  return (
    <span
      className={cn(
        "inline-flex items-center border",
        size === "lg"
          ? "rounded-md px-3 py-1 text-sm font-semibold"
          : "rounded-full px-2 py-0.5 text-xs font-medium",
        className,
      )}
    >
      {label}
    </span>
  );
}

export function TransferLocationRoute({
  originLocation,
  originLocationId,
  destinationLocation,
  destinationLocationId,
  size = "md",
}: {
  originLocation?: TransferBoardLocation | undefined;
  originLocationId: number;
  destinationLocation?: TransferBoardLocation | undefined;
  destinationLocationId: number;
  size?: "md" | "lg";
}): JSX.Element {
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <TransferLocationChip location={originLocation} locationId={originLocationId} size={size} />
      <span
        className={cn(
          "font-medium text-slate-400",
          size === "lg" ? "text-base" : "text-xs",
        )}
        aria-hidden
      >
        →
      </span>
      <TransferLocationChip
        location={destinationLocation}
        locationId={destinationLocationId}
        size={size}
      />
    </span>
  );
}

/** Off vs on for Kodiak, SGS, and Suntronic filter chips. Off stays an outline so the site color is still recognizable. */
function prominentTransferSiteFilterClass(
  location: TransferBoardLocation | undefined,
  active: boolean,
): string {
  const siteCode = location?.mrp_site_code?.trim().toLowerCase() ?? "";
  if (siteCode === "sgs") {
    return active
      ? "border-sky-600 bg-sky-200 text-sky-950 ring-2 ring-sky-600 ring-offset-1"
      : "border-sky-300 bg-white text-sky-800 hover:bg-sky-50";
  }
  if (siteCode === "suntronic") {
    return active
      ? "border-amber-600 bg-amber-200 text-amber-950 ring-2 ring-amber-600 ring-offset-1"
      : "border-amber-300 bg-white text-amber-900 hover:bg-amber-50";
  }
  return active
    ? "border-violet-600 bg-violet-200 text-violet-950 ring-2 ring-violet-600 ring-offset-1"
    : "border-violet-300 bg-white text-violet-900 hover:bg-violet-50";
}

export function transferLocationFilterToggleClass(
  active: boolean,
  location: TransferBoardLocation | undefined,
  _locationId: number,
  prominent = false,
): string {
  const shape = "rounded-full border px-3 py-1 text-xs font-semibold transition";
  if (prominent) {
    return cn(shape, prominentTransferSiteFilterClass(location, active));
  }
  if (active) {
    return cn(
      shape,
      "border-slate-600 bg-slate-200 text-slate-950 shadow-sm ring-2 ring-slate-500 ring-offset-1",
    );
  }
  return cn(shape, "border-slate-300 bg-white font-medium text-slate-500 hover:border-slate-400");
}

export function TransferLocationFilterButtons(props: {
  locations: readonly TransferBoardLocation[];
  idPrefix: string;
  isActive: (locationId: number) => boolean;
  onToggle: (locationId: number) => void;
}): JSX.Element {
  const sorted = sortTransferBoardLocationsForFilters(props.locations);
  const prominent = sorted.filter((location) => isProminentTransferPlanningSite(location));
  const other = sorted.filter((location) => !isProminentTransferPlanningSite(location));

  const renderButton = (location: TransferBoardLocation, prominentSite: boolean): JSX.Element => {
    const active = props.isActive(location.id);
    return (
      <button
        key={`${props.idPrefix}-${String(location.id)}`}
        type="button"
        aria-pressed={active}
        onClick={() => props.onToggle(location.id)}
        className={transferLocationFilterToggleClass(active, location, location.id, prominentSite)}
      >
        {buildTransferLocationLabel(location, location.id)}
      </button>
    );
  };

  return (
    <div className="mt-1 flex flex-wrap items-center gap-1">
      {prominent.map((location) => renderButton(location, true))}
      {prominent.length > 0 && other.length > 0 ? (
        <span className="mx-0.5 select-none px-0.5 text-sm font-light text-slate-300" aria-hidden>
          |
        </span>
      ) : null}
      {other.map((location) => renderButton(location, false))}
    </div>
  );
}
